import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { openWhatsApp, WHATSAPP_MESSAGES } from '../../utils/whatsapp';

export const ChatSupport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentShown && !isOpen) {
        setShowExitIntent(true);
        setExitIntentShown(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [exitIntentShown, isOpen]);

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div
          data-testid="chat-panel"
          className="fixed"
          style={{
            bottom: '96px',
            right: '24px',
            zIndex: 10000,
          }}
        >
          <div
            className="w-80 overflow-hidden"
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)',
            }}
          >
            {/* Header */}
            <div
              className="px-6 py-5 flex items-center justify-between"
              style={{ backgroundColor: '#1A3D36' }}
            >
              <div>
                <h3
                  className="text-sm font-serif font-semibold"
                  style={{ color: '#FFFFFF' }}
                >
                  Life Reversal
                </h3>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  How can we help you?
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:opacity-70 transition-opacity"
                data-testid="chat-close"
                type="button"
              >
                <X size={18} style={{ color: '#FFFFFF' }} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              <p
                className="text-sm mb-5 leading-relaxed"
                style={{ color: '#6B6B6B' }}
              >
                Get structured guidance for your protocol
              </p>

              <button
                type="button"
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.homepage)}
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium transition-all duration-200 hover:opacity-90"
                style={{
                  backgroundColor: '#1A3D36',
                  color: '#FFFFFF',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                }}
                data-testid="whatsapp-cta"
              >
                Chat on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent Overlay */}
      {showExitIntent && (
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 10001, backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => setShowExitIntent(false)}
          />
          <div
            className="fixed"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10002,
              width: '100%',
              maxWidth: '400px',
            }}
            data-testid="exit-intent-popup"
          >
            <div
              className="overflow-hidden mx-4"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                boxShadow: '0 24px 60px rgba(0, 0, 0, 0.18)',
              }}
            >
              <div
                className="px-8 py-6"
                style={{ backgroundColor: '#1A3D36' }}
              >
                <h3
                  className="font-serif text-xl mb-1"
                  style={{ color: '#FFFFFF' }}
                >
                  Get a personalized protocol
                </h3>
                <p
                  className="text-sm"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Talk to our team before you go
                </p>
              </div>
              <div className="px-8 py-6">
                <p
                  className="text-sm mb-5 leading-relaxed"
                  style={{ color: '#6B6B6B' }}
                >
                  Our protocol specialists can help you choose the right products for your goals.
                </p>
                <button
                  type="button"
                  onClick={() => openWhatsApp(WHATSAPP_MESSAGES.exitIntent)}
                  className="w-full py-3 text-sm font-medium transition-all duration-200 hover:opacity-90 mb-3"
                  style={{
                    backgroundColor: '#1A3D36',
                    color: '#FFFFFF',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  data-testid="exit-intent-whatsapp"
                >
                  Chat with a Specialist
                </button>
                <button
                  type="button"
                  onClick={() => setShowExitIntent(false)}
                  className="w-full py-2 text-sm transition-opacity hover:opacity-70"
                  style={{
                    color: '#6B6B6B',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  data-testid="exit-intent-dismiss"
                >
                  No thanks, I'll keep browsing
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Chat Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 9999 }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-button"
        type="button"
        className="fixed flex items-center justify-center transition-all duration-200"
        style={{
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: '#1A3D36',
          boxShadow: '0 4px 16px rgba(26, 61, 54, 0.3)',
          transform: isOpen ? 'scale(0.95)' : 'scale(1)',
          zIndex: 10000,
          border: 'none',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(26, 61, 54, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = isOpen ? 'scale(0.95)' : 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(26, 61, 54, 0.3)';
        }}
      >
        {isOpen ? (
          <X size={22} style={{ color: '#FFFFFF' }} />
        ) : (
          <MessageCircle size={22} style={{ color: '#FFFFFF' }} />
        )}
      </button>
    </>
  );
};

export default ChatSupport;
